import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EduzyLogo } from "@/components/ui/EduzyLogo";
import {
  LogOut,
  Menu,
  X,
  ChevronRight,
  Home,
} from "lucide-react";

/**
 * Portal color schemes matching the overall website theme
 * 
 * Teacher: Blue (matches EduWise)
 * Parent: Indigo/Purple (matches EduAssist) 
 * Student: Emerald/Green (fresh, growth)
 * School: Teal (professional, trust)
 */
export const PORTAL_THEMES = {
  teacher: {
    name: "Teacher",
    primary: "blue",
    gradient: "from-blue-600 to-blue-700",
    gradientLight: "from-blue-50 to-blue-100",
    bg: "bg-blue-600",
    bgLight: "bg-blue-50",
    bgHover: "hover:bg-blue-700",
    text: "text-blue-600",
    textLight: "text-blue-100",
    border: "border-blue-200",
    activeNav: "bg-blue-100 text-blue-700",
    activeNavDark: "bg-blue-700 text-white",
    ring: "ring-blue-500",
    accent: "#2563eb",
  },
  parent: {
    name: "Parent",
    primary: "indigo",
    gradient: "from-indigo-600 to-purple-600",
    gradientLight: "from-indigo-50 to-purple-50",
    bg: "bg-indigo-600",
    bgLight: "bg-indigo-50",
    bgHover: "hover:bg-indigo-700",
    text: "text-indigo-600",
    textLight: "text-indigo-100",
    border: "border-indigo-200",
    activeNav: "bg-indigo-100 text-indigo-700",
    activeNavDark: "bg-indigo-700 text-white",
    ring: "ring-indigo-500",
    accent: "#4f46e5",
  },
  student: {
    name: "Student",
    primary: "emerald",
    gradient: "from-emerald-600 to-green-600",
    gradientLight: "from-emerald-50 to-green-50",
    bg: "bg-emerald-600",
    bgLight: "bg-emerald-50",
    bgHover: "hover:bg-emerald-700",
    text: "text-emerald-600",
    textLight: "text-emerald-100",
    border: "border-emerald-200",
    activeNav: "bg-emerald-100 text-emerald-700",
    activeNavDark: "bg-emerald-700 text-white",
    ring: "ring-emerald-500",
    accent: "#059669",
  },
  school: {
    name: "School",
    primary: "teal",
    gradient: "from-teal-600 to-cyan-600",
    gradientLight: "from-teal-50 to-cyan-50",
    bg: "bg-teal-600",
    bgLight: "bg-teal-50",
    bgHover: "hover:bg-teal-700",
    text: "text-teal-600",
    textLight: "text-teal-100",
    border: "border-teal-200",
    activeNav: "bg-teal-100 text-teal-700",
    activeNavDark: "bg-teal-700 text-white",
    ring: "ring-teal-500",
    accent: "#0d9488",
  },
};

/**
 * Unified Portal Header Component
 * Used across Teacher, Parent, Student, and School portals
 */
export function PortalHeader({ 
  portalType, 
  userName, 
  userSubtitle,
  onLogout,
  rightContent,
  onMenuClick,
  showMobileMenu = true,
}) {
  const theme = PORTAL_THEMES[portalType] || PORTAL_THEMES.teacher;
  const navigate = useNavigate();

  return (
    <header className={`bg-gradient-to-r ${theme.gradient} text-white shadow-lg sticky top-0 z-50`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Left: Logo + Portal Name */}
          <div className="flex items-center gap-4">
            {showMobileMenu && (
              <Button 
                variant="ghost" 
                size="icon" 
                className="lg:hidden text-white hover:bg-white/20"
                onClick={onMenuClick}
              >
                <Menu className="h-5 w-5" />
              </Button>
            )}
            <div 
              className="flex items-center gap-3 cursor-pointer" 
              onClick={() => navigate("/")}
            >
              <img 
                src="/eduzy-logo-optimized.png" 
                alt="Eduzy" 
                className="h-8 w-auto brightness-0 invert"
              />
              <div className="hidden sm:block">
                <span className={`text-sm font-medium ${theme.textLight}`}>
                  {theme.name} Portal
                </span>
              </div>
            </div>
          </div>

          {/* Right: User Info + Actions */}
          <div className="flex items-center gap-4">
            {rightContent}
            
            <div className="hidden sm:flex items-center gap-3">
              <div className="text-right">
                <p className="text-sm font-medium text-white">{userName}</p>
                {userSubtitle && (
                  <p className={`text-xs ${theme.textLight}`}>{userSubtitle}</p>
                )}
              </div>
            </div>

            <Button
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/20"
              onClick={onLogout}
            >
              <LogOut className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
}

/**
 * Unified Portal Sidebar Component
 */
export function PortalSidebar({
  portalType,
  navItems,
  userName,
  userSubtitle,
  userAvatar,
  isOpen,
  onClose,
  onLogout,
  footerContent,
}) {
  const theme = PORTAL_THEMES[portalType] || PORTAL_THEMES.teacher;
  const location = useLocation();

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:sticky top-0 left-0 z-50 lg:z-30 h-screen w-64 bg-white border-r border-gray-200
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
      `}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className={`p-4 border-b border-gray-200 bg-gradient-to-r ${theme.gradientLight}`}>
            <div className="flex items-center justify-between lg:hidden mb-3">
              <span className={`font-semibold ${theme.text}`}>{theme.name} Portal</span>
              <Button variant="ghost" size="sm" onClick={onClose}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            {/* User Info */}
            <div className="flex items-center gap-3">
              {userAvatar ? (
                <div className={`w-10 h-10 rounded-full ${theme.bgLight} flex items-center justify-center`}>
                  {typeof userAvatar === 'string' ? (
                    <span className={`text-sm font-semibold ${theme.text}`}>{userAvatar}</span>
                  ) : (
                    userAvatar
                  )}
                </div>
              ) : (
                <div className={`w-10 h-10 rounded-full ${theme.bgLight} flex items-center justify-center`}>
                  <span className={`text-sm font-semibold ${theme.text}`}>
                    {userName?.charAt(0) || "U"}
                  </span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">{userName}</p>
                {userSubtitle && (
                  <p className="text-xs text-gray-500 truncate">{userSubtitle}</p>
                )}
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path || 
                (item.matchPaths && item.matchPaths.some(p => location.pathname.startsWith(p)));
              const Icon = item.icon;
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={onClose}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                    isActive 
                      ? `${theme.activeNav} font-medium` 
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                  {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                </Link>
              );
            })}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-gray-200">
            {footerContent}
            <Button
              variant="ghost"
              className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={onLogout}
            >
              <LogOut className="w-5 h-5 mr-3" />
              Logout
            </Button>
          </div>
        </div>
      </aside>
    </>
  );
}

/**
 * Unified Portal Footer Component
 */
export function PortalFooter({ portalType }) {
  const theme = PORTAL_THEMES[portalType] || PORTAL_THEMES.teacher;
  const currentYear = new Date().getFullYear();

  return (
    <footer className={`bg-white border-t border-gray-200 py-4 px-6 mt-auto`}>
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <img 
            src="/eduzy-logo-optimized.png" 
            alt="Eduzy" 
            className="h-6 w-auto opacity-60"
          />
          <p className="text-sm text-gray-500">
            &copy; {currentYear} Eduzy by Contentium Tech
          </p>
        </div>
        <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-500">
          <a href="/privacy" className="hover:text-blue-600 transition-colors">Privacy</a>
          <a href="/terms" className="hover:text-blue-600 transition-colors">Terms</a>
          <a href="/faq" className="hover:text-blue-600 transition-colors">FAQ</a>
          <a href="mailto:support@eduzy.co" className="hover:text-blue-600 transition-colors">Support</a>
        </div>
      </div>
    </footer>
  );
}

/**
 * Logout Confirmation Modal
 */
export function LogoutModal({ 
  open, 
  onOpenChange, 
  onConfirm, 
  portalType = "teacher" 
}) {
  const theme = PORTAL_THEMES[portalType] || PORTAL_THEMES.teacher;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LogOut className="h-5 w-5 text-orange-600" />
            Confirm Logout
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to logout from the {theme.name} Portal?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex gap-2 sm:justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm}>
            <LogOut className="h-4 w-4 mr-2" />
            Yes, Logout
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Back to Home Button for all portals
 */
export function BackToHomeButton({ portalType }) {
  const theme = PORTAL_THEMES[portalType] || PORTAL_THEMES.teacher;
  const navigate = useNavigate();

  return (
    <Button
      variant="ghost"
      size="sm"
      className={`${theme.text} hover:${theme.bgLight}`}
      onClick={() => navigate("/")}
    >
      <Home className="h-4 w-4 mr-2" />
      Back to Home
    </Button>
  );
}
